import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { HttpApiService } from '@recruitee/http';
import {
  CandidateReference,
  OfferBriefReference,
  Placement,
  Position,
  StageReference,
} from '@recruitee/http/types';

export type PlacementHttpReference = CandidateReference | OfferBriefReference | StageReference;
export interface PlacementChangeStageParams {
  id: number;
  stageId: number | string;
  runActions?: boolean | null;
  proceed?: boolean;
  hiredAt?: string;
  jobStartsAt?: string;
  openingId?: number | null;
}
export interface PlacementDisqualifyParams {
  id: number;
  disqualifyReasonId: number | string;
  runActions?: boolean | null;
}
export interface UpdateHiringDetailsParams {
  placementId: number;
  hiredAt: string;
  jobStartsAt: string;
  openingId: number | null;
}

@Injectable({ providedIn: 'root' })
export class PlacementHttpClient {
  constructor(protected httpApi: HttpApiService) {}

  public create({
    offerId,
    candidateId,
  }: {
    offerId: number;
    candidateId: number;
  }): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.post(`/placements`, { offer_id: offerId, candidate_id: candidateId });
  }

  public delete({
    id,
  }: {
    id: number;
  }): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.delete(`/placements/${id}`);
  }

  public updatePositions(
    positions: Position[],
  ): Observable<{ placements: Placement[]; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/update_positions`, null, {
      placements: positions,
    });
  }

  public updateCandidatePositions(
    positions: Position[],
  ): Observable<{ placements: Placement[]; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/update_candidate_positions`, null, {
      placements: positions,
    });
  }

  public requalify(
    id: number,
  ): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/${id}/requalify`);
  }

  public disqualify({
    id,
    disqualifyReasonId,
    runActions = null,
  }: PlacementDisqualifyParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${id}/disqualify`, {
      disqualifyReasonId,
      ...(runActions !== null && { runActions }),
    });
  }

  public changeStage({
    id,
    stageId,
    runActions = null,
    proceed = false,
    hiredAt = '',
    jobStartsAt = '',
    openingId = null,
  }: PlacementChangeStageParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${id}/change_stage`, {
      stage_id: stageId,
      ...(runActions !== null && { run_actions: runActions }),
      proceed,
      hiredAt,
      jobStartsAt,
      openingId,
    });
  }

  public fetchHiringDetails(placementId: number): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.get(`/placements/${placementId}/hiring_details`);
  }

  public updateHiringDetails({
    placementId,
    hiredAt,
    jobStartsAt,
    openingId,
  }: UpdateHiringDetailsParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${placementId}/hiring_details`, null, {
      placement: {
        hiredAt,
        ...(jobStartsAt?.length ? { jobStartsAt } : {}),
        openingId,
      },
    });
  }
}
